import { useMemo, useState } from "react";

import { cn } from "../../../../lib/utils";
import { Popover, PopoverContent, PopoverTrigger } from "../../../ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "../../../ui/command";
import {
  dropdownChevronClassName,
  dropdownTriggerClassName,
} from "../../../ui/dropdown-trigger-styles";
import { useTriggerWidth } from "../../../ui/use-trigger-width";

import AccountAvatar from "../../../AccountAvatar/AccountAvatar";
import {
  splitAccounts,
  getAccountNameParts,
  filterAccountsByText,
} from "../../../../utils/accounts";
import { getPlatformNameType } from "../../../../utils/accounts";
import { AccountType, AccountGroup } from "../../../../shared/types/accounts";
import {
  doesAccountNeedApiReconnect,
  isAccountFailedForPosting,
  isAccountPostingAndInsightsEnabled,
} from "../../../../entities/accounts/libs/get-account-statuses";
import { useInstagramApiPublishingOn } from "../../../../utils/feature-toggle-utils";

export interface AccountComboboxProps {
  accounts: AccountType[];
  selectedAccountId?: string | null;
  onSelect: (account: AccountType) => void;
  placeholder?: string;
  /** Show search input. If undefined, auto-enables when accounts > searchThreshold */
  searchable?: boolean;
  /** Threshold for auto-enabling search (default: 10) */
  searchThreshold?: number;
  disabled?: boolean;
  className?: string;
}

interface AccountItemProps {
  account: AccountType;
  isSelected: boolean;
  onSelect: () => void;
}

const AccountItem = ({
  account,
  isSelected,
  onSelect,
}: AccountItemProps): JSX.Element => {
  const [, name] = getAccountNameParts(account);
  const { platformType, platformName } = getPlatformNameType(account);

  // TODO: variable name is inverted — `needReconnection` is true when the account
  // does NOT need API reconnect AND is NOT posting-enabled (i.e. account is in a
  // "dead" state not yet flagged by doesAccountNeedApiReconnect). Consider renaming
  // to `isUnflaggedDisconnect` or inverting the logic for clarity.
  const instagramApiPublishing = useInstagramApiPublishingOn();
  const needReconnection = !(
    doesAccountNeedApiReconnect(account, { instagramApiPublishing }) ||
    isAccountPostingAndInsightsEnabled(account, { instagramApiPublishing })
  );

  const disconnected = useMemo(() => {
    return (
      isAccountFailedForPosting(account, { instagramApiPublishing }) ||
      account?.promptApiReconnect
    );
  }, [account, instagramApiPublishing]);

  return (
    <CommandItem
      value={`${account._id}-${name}`}
      onSelect={onSelect}
      className={cn(
        "flex items-center gap-2 px-2 py-1.5 cursor-pointer rounded-sm",
        "data-[selected=true]:bg-blue-50",
        (disconnected || needReconnection) && "opacity-60"
      )}
    >
      <AccountAvatar
        profilePictureUrl={account.profilePictureUrl}
        platformType={platformType}
        color={account.color}
        size="small"
        placement="top"
        tooltip={platformName}
        disconnected={disconnected || needReconnection}
        useYtRedPlayIcon
      />
      <span className="flex-1 truncate text-left font-inter text-sm text-neutral-700">
        {name}
      </span>
      {isSelected && (
        <i className="fa-solid fa-check h-4 w-4 shrink-0 text-blue-500" />
      )}
    </CommandItem>
  );
};

export const AccountCombobox = ({
  accounts,
  selectedAccountId,
  onSelect,
  placeholder = "Choose account...",
  searchable,
  searchThreshold = 10,
  disabled = false,
  className,
}: AccountComboboxProps): JSX.Element => {
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const instagramApiPublishing = useInstagramApiPublishingOn();
  const { triggerRef, triggerWidthStyle } =
    useTriggerWidth<HTMLButtonElement>(open);

  // Auto-enable search if accounts exceed threshold (unless explicitly set)
  const showSearch = useMemo(() => {
    if (searchable !== undefined) return searchable;
    return accounts.length > searchThreshold;
  }, [searchable, accounts.length, searchThreshold]);

  const selectedAccount = useMemo(() => {
    return accounts.find((acc) => acc._id === selectedAccountId);
  }, [accounts, selectedAccountId]);

  const { groupedAccounts, otherAccounts } = useMemo(
    () => splitAccounts(accounts),
    [accounts]
  );

  // Filter accounts based on search
  const filteredGroupedAccounts = useMemo(() => {
    if (!searchQuery) return groupedAccounts;
    return groupedAccounts
      .map((group) => ({
        ...group,
        accounts: group.accounts.filter(filterAccountsByText(searchQuery)),
      }))
      .filter((group) => group.accounts.length > 0);
  }, [groupedAccounts, searchQuery]);

  const filteredOtherAccounts = useMemo(() => {
    if (!searchQuery) return otherAccounts;
    return otherAccounts.filter(filterAccountsByText(searchQuery));
  }, [otherAccounts, searchQuery]);

  const handleSelect = (account: AccountType) => {
    onSelect(account);
    setOpen(false);
    setSearchQuery("");
  };

  const renderTriggerContent = () => {
    if (selectedAccount) {
      const [, name] = getAccountNameParts(selectedAccount);
      const { platformType, platformName } =
        getPlatformNameType(selectedAccount);

      // TODO: see comment on AccountItem — variable name is inverted
      const needReconnection = !(
        doesAccountNeedApiReconnect(selectedAccount, {
          instagramApiPublishing,
        }) ||
        isAccountPostingAndInsightsEnabled(selectedAccount, {
          instagramApiPublishing,
        })
      );

      const disconnected =
        isAccountFailedForPosting(selectedAccount, {
          instagramApiPublishing,
        }) || selectedAccount?.promptApiReconnect;

      return (
        <>
          <AccountAvatar
            profilePictureUrl={selectedAccount.profilePictureUrl}
            platformType={platformType}
            color={selectedAccount.color}
            size="small"
            placement="top"
            tooltip={platformName}
            disconnected={disconnected || needReconnection}
            useYtRedPlayIcon
          />
          <span className="flex-1 truncate text-left font-inter text-sm text-neutral-800">
            {name}
          </span>
        </>
      );
    }

    return (
      <span className="flex-1 text-left font-inter text-sm text-neutral-400">
        {placeholder}
      </span>
    );
  };

  const hasNoResults =
    filteredGroupedAccounts.length === 0 && filteredOtherAccounts.length === 0;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          ref={triggerRef}
          type="button"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className={cn(
            "h-10 w-full border px-3",
            "font-inter text-sm",
            "flex items-center justify-between gap-2",
            "transition-all duration-200",
            dropdownTriggerClassName,
            disabled && "opacity-50 cursor-not-allowed",
            className
          )}
        >
          <div className="flex items-center gap-2 flex-1 min-w-0">
            {renderTriggerContent()}
          </div>
          <i
            className={cn(
              "fa-solid fa-chevron-down h-4 w-4 shrink-0 transition-transform",
              dropdownChevronClassName,
              open && "rotate-180"
            )}
          />
        </button>
      </PopoverTrigger>
      <PopoverContent
        className="p-0 border-neutral-200"
        disableAnimation
        style={triggerWidthStyle}
        align="start"
        sideOffset={4}
      >
        <Command shouldFilter={false}>
          {showSearch && (
            <CommandInput
              placeholder="Search accounts..."
              value={searchQuery}
              onValueChange={setSearchQuery}
              className="font-inter border-b border-neutral-200"
            />
          )}
          <CommandList className="max-h-[300px]">
            {hasNoResults && (
              <CommandEmpty className="py-6">
                <div className="flex flex-col items-center gap-2">
                  <i className="fa-regular fa-magnifying-glass h-5 w-5 text-neutral-400" />
                  <span className="font-inter text-sm text-neutral-500">
                    No accounts found
                  </span>
                </div>
              </CommandEmpty>
            )}

            {/* Grouped accounts (brands) */}
            {filteredGroupedAccounts.map((group: AccountGroup) => (
              <CommandGroup
                key={group.title}
                heading={group.title}
                className="[&_[cmdk-group-heading]]:font-inter [&_[cmdk-group-heading]]:text-xs [&_[cmdk-group-heading]]:font-medium [&_[cmdk-group-heading]]:text-neutral-500 [&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:py-1.5"
              >
                {group.accounts.map((account) => (
                  <AccountItem
                    key={account._id}
                    account={account}
                    isSelected={account._id === selectedAccountId}
                    onSelect={() => handleSelect(account)}
                  />
                ))}
              </CommandGroup>
            ))}

            {/* Ungrouped accounts */}
            {filteredOtherAccounts.length > 0 && (
              <CommandGroup>
                {filteredOtherAccounts.map((account) => (
                  <AccountItem
                    key={account._id}
                    account={account}
                    isSelected={account._id === selectedAccountId}
                    onSelect={() => handleSelect(account)}
                  />
                ))}
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

export default AccountCombobox;
